import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { User } from '@/types/user';
import { useApi } from '@/hooks/useApi';

interface ExternalUser {
  id: string;
  firstName: string;
  lastName: string;
}

interface ManageUserIntegrationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onSave: (userId: string, externalUserId: string, firstName: string, lastName: string) => void;
}

export function ManageUserIntegrationsDialog({
  open,
  onOpenChange,
  user,
  onSave,
}: ManageUserIntegrationsDialogProps) {
  const { apiGet } = useApi();
  
    const [externalUsers, setExternalUsers] = useState<ExternalUser[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [usersError, setUsersError] = useState<string | null>(null);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  useEffect(() => {
    if (open && user) {
      setSelectedUserId(user.externalUserId || '');
      setIsLoadingUsers(true);
      setUsersError(null);
      
            const includeParam = user.externalUserId ? `?includeUserId=${encodeURIComponent(user.externalUserId)}` : '';
            apiGet<ExternalUser[]>(`/api/v1/externalUsers${includeParam}`)
              .then((data) => {
                setExternalUsers(data);
                setIsLoadingUsers(false);
              })
        .catch((error) => {
          console.error('Error fetching external users:', error);
          setUsersError('Failed to load users. Please try again.');
          setIsLoadingUsers(false);
        });
    }
  }, [open, user, apiGet]);

    const isLinked = !!user?.externalUserId;

    const handleSave = () => {
      if (!user) return;

      const selectedExternalUser = externalUsers.find(u => u.id === selectedUserId);
      const firstName = selectedExternalUser?.firstName || '';
      const lastName = selectedExternalUser?.lastName || '';

      onSave(user.id, selectedUserId, firstName, lastName);
      onOpenChange(false);
    };

        const handleRemoveLinkClick = () => {
          setShowRemoveConfirm(true);
        };

        const handleConfirmRemoveLink = () => {
          if (!user) return;
          onSave(user.id, '', '', '');
          setShowRemoveConfirm(false);
          onOpenChange(false);
        };

    const handleCancel = () => {
      setSelectedUserId('');
      onOpenChange(false);
    };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Manage Integrations</DialogTitle>
          <DialogDescription>
            Link <strong>{user?.firstName} {user?.lastName}</strong> to an Evo user.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="evo-user">Evo Users</Label>
            {isLoadingUsers ? (
              <div className="text-sm text-muted-foreground">Loading Users...</div>
            ) : usersError ? (
              <div className="text-sm text-destructive">{usersError}</div>
            ) : (
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an Evo user" />
                </SelectTrigger>
                <SelectContent>
                  {externalUsers.map((externalUser) => (
                    <SelectItem key={externalUser.id} value={externalUser.id}>
                      {externalUser.firstName} {externalUser.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
                <DialogFooter className="flex justify-between sm:justify-between">
                  <div>
                    {isLinked && (
                                            <Button variant="outline" className="text-destructive hover:text-destructive" onClick={handleRemoveLinkClick}>
                                              Remove Link
                                            </Button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={isLoadingUsers}>
                      Save
                    </Button>
                  </div>
                </DialogFooter>
      </DialogContent>

      <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Integration Link</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove the integration link for <strong>{user?.firstName} {user?.lastName}</strong>? This will unlink them from the external Evo user.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRemoveLink} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remove Link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
